import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserIcon,
  HomeIcon,
  Cog6ToothIcon,
  PlusIcon,
  Bars3Icon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  SparklesIcon,
  ArrowRightIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';

import { AuthContext } from '../contexts/AuthContext';
import { ref, onValue, update } from 'firebase/database';
import { database } from '../config/firebase';
import { listenToAccessibleDocuments, DesignDocument, deleteDocument } from '../utils/documentService';
import { TrashIcon } from '@heroicons/react/24/solid';
import CreateTeamModal from '../components/CreateTeamModal';
import UpgradeToProModal from '../components/UpgradeToProModal';
import { subscribePlanCounts, canCreateDesign, canCreateOrJoinTeam, subscribeUserPlan, isPro, UserPlan } from '../utils/planGate';
import { listenToUserDocuments } from '../utils/documentService';
import AppHeader from '../components/AppHeader';
import AppFooter from '../components/AppFooter';
import InviteNotifications from '../components/InviteNotifications';
import activityService, { UserActivity } from '../utils/activityService';

// Notification Toast Component
const Toast: React.FC<{ message: string; onClose: () => void }> = ({ message, onClose }) => (
  <div className="fixed top-6 right-6 z-50 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-4 animate-fade-in">
    <span>{message}</span>
    <button onClick={onClose} className="ml-2 text-white font-bold">&times;</button>
  </div>
);

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [templates, setTemplates] = useState<DesignDocument[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [toast, setToast] = useState<{ message: string } | null>(null);
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [myDesigns, setMyDesigns] = useState<DesignDocument[]>([]);
  const [loadingDesigns, setLoadingDesigns] = useState(true);
  const [planCounts, setPlanCounts] = useState<{ designCount: number; teamCount: number }>({ designCount: 0, teamCount: 0 });
  const [showUpgradeModal, setShowUpgradeModal] = useState<false | 'design' | 'team'>(false);
  const [plan, setPlan] = useState<UserPlan>('free');
  const [search, setSearch] = useState('');
  const [activities, setActivities] = useState<UserActivity[]>([]);

  // First three recent designs
  const recentTemplates = templates.slice(0, 3);
  const currentUser = authContext?.user;

  // Listen to personal and team documents
  useEffect(() => {
    if (!currentUser?.uid) return;
    // Extract team IDs the user belongs to
    const teamIds = teams.map(t => t.id);
    const unsub = listenToAccessibleDocuments(currentUser.uid, teamIds, (docs) => {
      // Sort by last_modified descending
      const sorted = docs.sort((a, b) => b.last_modified - a.last_modified);
      console.log('Accessible documents loaded:', docs.length, 'for user:', currentUser.uid);
      console.log('Documents:', docs.map(d => ({ id: d.id, name: d.name, owner_uid: d.owner_uid })));
      setTemplates(sorted);
    });
    return () => { if (unsub) unsub(); };
  }, [currentUser?.uid, teams]);

  // Fetch teams from Firebase (only teams where current user is a member)
  useEffect(() => {
    if (!currentUser?.uid) return;
    const teamsRef = ref(database, 'teams');
    return onValue(teamsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const teamsList = Object.entries(data)
          .map(([id, team]: [string, any]) => ({ id, ...team }))
          .filter(team => team.members && Object.keys(team.members).includes(currentUser.uid));
        setTeams(teamsList);
      } else {
        setTeams([]);
      }
    });
  }, [currentUser?.uid]);

  // Subscribe to counts for plan gating
  useEffect(() => {
    if (!currentUser?.uid) return;
    const unsub = subscribePlanCounts(currentUser.uid, (counts) => setPlanCounts(counts));
    return () => { if (unsub) unsub(); };
  }, [currentUser?.uid]);

  // Subscribe to user plan
  useEffect(() => {
    if (!currentUser?.uid) return;
    const unsub = subscribeUserPlan(currentUser.uid, (p) => setPlan(p));
    return () => { if (unsub) unsub(); };
  }, [currentUser?.uid]);

  // Recent activities (last 7 days)
  useEffect(() => {
    if (!currentUser?.uid) return;
    activityService.getUserActivities(currentUser.uid, (list) => setActivities(list));
  }, [currentUser?.uid]);



  // Show toast for 3 seconds
  const showToast = (message: string) => {
    setToast({ message });
    setTimeout(() => setToast(null), 3000);
  };

  // Upcoming meetings feature removed in the new design – placeholder retained for future implementation if needed.

  useEffect(() => {
    if (!currentUser?.uid) return;
    setLoadingDesigns(true);
    
    // Listen to user documents from Realtime Database
    const unsubscribe = listenToUserDocuments(currentUser.uid, (docs) => {
      console.log('User documents loaded:', docs.length, 'for user:', currentUser.uid);
      console.log('User documents:', docs.map(d => ({ id: d.id, name: d.name, owner_uid: d.owner_uid })));
      setMyDesigns(docs.sort((a, b) => b.last_modified - a.last_modified));
      setLoadingDesigns(false);
    });
    
    return () => { if (unsubscribe) unsubscribe(); };
  }, [currentUser?.uid]);

  if (!authContext) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {toast && <Toast message={toast.message} onClose={() => setToast(null)} />}
      {/* Header */}
      <AppHeader />

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="sm:hidden fixed inset-0 z-50 bg-black bg-opacity-50" onClick={() => setIsMobileMenuOpen(false)}>
          <div className="absolute right-0 top-0 h-full w-64 bg-white shadow-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-col space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Menu</h3>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 rounded-lg hover:bg-gray-100"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex flex-col space-y-4">
                <button
                  onClick={() => {
                    navigate('/dashboard');
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <HomeIcon className="w-5 h-5 text-gray-600" />
                  <span className="text-gray-700">Dashboard</span>
                </button>
                <button
                  onClick={() => {
                    navigate('/profile');
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <UserIcon className="w-5 h-5 text-gray-600" />
                  <span className="text-gray-700">Profile</span>
                </button>
                <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 transition-colors">
                  <InviteNotifications />
                  <span className="text-gray-700">Notifications</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}



      {/* Main Content */}
      <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10">
        <div className="max-w-7xl mx-auto">
          {/* Welcome Section */}
          <div className="mb-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
              Welcome back, {authContext.user?.displayName || 'User'}! 👋
            </h1>
            <p className="text-gray-600 text-lg">
              Ready to collaborate with your team? Start a new collaboration or join an existing one.
            </p>
          </div>

          {/* Start a New Collaboration */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 sm:p-8 mb-8 text-white shadow-lg">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold mb-1">Start a New Collaboration</h2>
                <p className="text-blue-100 text-sm sm:text-base">Create a collaborative space for your team to brainstorm, plan, and innovate together.</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  className="px-4 py-2 rounded-lg bg-white text-blue-700 font-semibold shadow hover:shadow-md transition"
                  onClick={() => {
                    if (!canCreateDesign(planCounts.designCount)) { setShowUpgradeModal('design'); return; }
                    navigate('/editor/new');
                  }}
                >
                  + New Design
                </button>
                <button
                  className="px-4 py-2 rounded-lg bg-white/10 text-white font-semibold border border-white/30 hover:bg-white/20 transition"
                  onClick={() => {
                    if (!canCreateOrJoinTeam(planCounts.teamCount)) { setShowUpgradeModal('team'); return; }
                    setShowCreateTeamModal(true);
                  }}
                >
                  + New Team
                </button>
              </div>
            </div>
          </div>

          {/* Plan usage and upgrade banner for free users */}
          {!isPro(plan) && (
            <div className="mb-8 bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-gray-900">You're on the Free plan</h3>
                  <p className="text-sm text-gray-600 mt-1">Use up to 3 personal designs and 3 teams. Upgrade for unlimited and downloads.</p>
                </div>
                <button
                  className="px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold hover:shadow-md"
                  onClick={() => setShowUpgradeModal('design')}
                >
                  Upgrade to Pro
                </button>
              </div>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>Designs</span>
                    <span>{planCounts.designCount} / 3</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500" style={{ width: `${Math.min(100, (planCounts.designCount/3)*100)}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>Teams</span>
                    <span>{planCounts.teamCount} / 3</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-indigo-500 to-fuchsia-500" style={{ width: `${Math.min(100, (planCounts.teamCount/3)*100)}%` }} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quick Start tiles */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <button
              onClick={() => {
                if (!canCreateDesign(planCounts.designCount)) { setShowUpgradeModal('design'); return; }
                navigate('/editor/new');
              }}
              className="group bg-white rounded-xl border border-gray-200 shadow-sm p-5 text-left hover:shadow-md transition"
            >
              <div className="flex items-center gap-3 mb-2">
                <Squares2X2Icon className="w-6 h-6 text-blue-600" />
                <h3 className="font-semibold text-gray-900">Create a Mood Board</h3>
              </div>
              <p className="text-sm text-gray-600">Start from a blank canvas and add images, notes and shapes.</p>
            </button>
            <button
              onClick={() => {
                if (!canCreateOrJoinTeam(planCounts.teamCount)) { setShowUpgradeModal('team'); return; }
                setShowCreateTeamModal(true);
              }}
              className="group bg-white rounded-xl border border-gray-200 shadow-sm p-5 text-left hover:shadow-md transition"
            >
              <div className="flex items-center gap-3 mb-2">
                <UserGroupIcon className="w-6 h-6 text-purple-600" />
                <h3 className="font-semibold text-gray-900">Invite Teammates</h3>
              </div>
              <p className="text-sm text-gray-600">Create a team and collaborate in real time.</p>
            </button>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active Teams</p>
                  <p className="text-2xl font-bold text-gray-900">{teams.length}</p>
                </div>
                <UserGroupIcon className="w-8 h-8 text-purple-500" />
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">My Designs</p>
                  <p className="text-2xl font-bold text-gray-900">{templates?.length || 0}</p>
                </div>
                <SparklesIcon className="w-8 h-8 text-green-500" />
              </div>
            </div>
          </div>

          {/* My Design Section (Firestore) */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">My Design</h2>
              <button
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg shadow hover:from-blue-600 hover:to-purple-600 transition-colors"
                onClick={() => {
                  if (!canCreateDesign(planCounts.designCount)) {
                    setShowUpgradeModal('design');
                    return;
                  }
                  navigate('/editor/new');
                }}
                title="Create New Mood Board"
              >
                <PlusIcon className="w-5 h-5" />
                <span className="hidden sm:inline">New Design</span>
              </button>
            </div>
            {/* Search */}
            <div className="mb-4 flex items-center gap-2 bg-white rounded-lg border border-gray-200 px-3 py-2 shadow-sm max-w-md">
              <MagnifyingGlassIcon className="w-5 h-5 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search designs..."
                className="w-full outline-none text-sm"
              />
            </div>
            {loadingDesigns ? (
              <div className="text-gray-500 text-center py-8">Loading...</div>
            ) : myDesigns.length === 0 ? (
              <div className="text-gray-500 text-center py-12">
                <div className="mx-auto w-24 h-24 rounded-2xl bg-gradient-to-br from-blue-100 to-purple-100 mb-4" />
                <div className="font-medium text-gray-700">No designs yet</div>
                <div className="text-sm">Click "New Design" to get started.</div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {myDesigns.filter(d => d.name.toLowerCase().includes(search.toLowerCase())).map((design) => (
                  <div
                    key={design.id}
                    className="relative bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow flex flex-col group"
                  >
                    {/* Delete button */}
                    <button
                      title="Delete Design"
                      onClick={async () => {
                        if (window.confirm('Delete this design permanently?')) {
                          await deleteDocument(design.id!);
                          // No need to update state manually as listenToUserDocuments will handle it
                        }
                      }}
                      className="absolute top-2 right-2 z-20 bg-gradient-to-r from-red-500 to-pink-500 text-white p-1.5 rounded-full shadow-lg transform transition-transform duration-200 group-hover:scale-110 hover:rotate-12 active:scale-90"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                    <div
                      className="relative h-32 mb-3 cursor-pointer"
                      onClick={() => navigate(`/editor/${design.id}`)}
                    >
                      {design.preview_image_url && !design.preview_image_url.includes('placeholder') ? (
                        <img
                          src={design.preview_image_url}
                          alt={design.name}
                          className="w-full h-full object-cover rounded-lg"
                          onError={(e) => {
                            // Fallback to placeholder if image fails to load
                            const target = e.target as HTMLImageElement;
                            target.src = 'https://via.placeholder.com/400x300/f3f4f6/6b7280?text=' + encodeURIComponent(design.name);
                          }}
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg flex flex-col items-center justify-center">
                          <div className="text-2xl font-bold text-blue-600 mb-2">
                            {design.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="text-xs text-blue-500">
                            Design
                          </div>
                        </div>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900 line-clamp-1 cursor-pointer" onClick={() => navigate(`/editor/${design.id}`)}>{design.name}</h3>
                    <p className="text-xs text-gray-500 mt-auto">Updated {new Date(design.last_modified).toLocaleDateString()}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Active Teams */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Active Teams</h2>
              <button
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg shadow hover:from-blue-600 hover:to-purple-600 transition-colors"
                onClick={() => {
                  if (!canCreateOrJoinTeam(planCounts.teamCount)) {
                    setShowUpgradeModal('team');
                    return;
                  }
                  setShowCreateTeamModal(true);
                }}
                title="Create New Team"
              >
                <PlusIcon className="w-5 h-5" />
                <span className="hidden sm:inline">New Team</span>
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {teams.length === 0 ? (
                <div className="col-span-3 flex flex-col items-center text-gray-500 py-8 gap-4">
                  <span>No teams found. Create your first team!</span>
                  <button
                    onClick={() => setShowCreateTeamModal(true)}
                    className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg shadow hover:from-blue-600 hover:to-purple-600 transition-colors"
                  >
                    Create Team
                  </button>
                </div>
              ) : (
                teams.map((team) => (
                  <div
                    key={team.id}
                    className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer flex flex-col gap-2"
                    onClick={() => navigate(`/teams/${team.id}`)}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center text-white font-semibold text-lg">
                        {team.name?.split(' ').map((w: string) => w[0]).join('').slice(0,2).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 text-lg">{team.name}</h3>
                        <p className="text-sm text-gray-500">{Object.keys(team.members || {}).length} members</p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mb-2">Created {new Date(team.created_at).toLocaleDateString()}</p>
                    {/* Entire card clicks handled by parent */}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Activity</h2>
            {activities.length === 0 ? (
              <div className="text-gray-500 text-sm">No activity from the last 7 days.</div>
            ) : (
              <ul className="bg-white border border-gray-200 rounded-xl divide-y shadow-sm">
                {activities.map((a) => (
                  <li key={a.id} className="p-3 text-sm flex items-center justify-between">
                    <span className="text-gray-700">
                      {a.description}: <span className="font-medium">{a.relatedName || a.relatedId}</span>
                    </span>
                    <span className="text-gray-400">{new Date(a.timestamp).toLocaleDateString()}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Onboarding checklist */}
          <div className="mb-8 bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Getting started</h2>
              <span className="text-sm text-gray-500">{[myDesigns.length>0, teams.length>0].filter(Boolean).length}/3 done</span>
            </div>
            <ul className="p-4 space-y-3 text-sm">
              <li className="flex items-center gap-3">
                <span className={`w-5 h-5 rounded-full ${myDesigns.length>0?'bg-emerald-500':'bg-gray-300'}`} />
                <span>Create your first design</span>
                {!myDesigns.length && (
                  <button onClick={()=>navigate('/editor/new')} className="ml-auto text-blue-600 font-medium hover:underline">Create</button>
                )}
              </li>
              <li className="flex items-center gap-3">
                <span className={`w-5 h-5 rounded-full ${teams.length>0?'bg-emerald-500':'bg-gray-300'}`} />
                <span>Create a team</span>
                {!teams.length && (
                  <button onClick={()=>setShowCreateTeamModal(true)} className="ml-auto text-blue-600 font-medium hover:underline">Create</button>
                )}
              </li>
              <li className="flex items-center gap-3">
                <span className={`w-5 h-5 rounded-full ${isPro(plan)?'bg-emerald-500':'bg-gray-300'}`} />
                <span>Enable downloads with Pro</span>
                {!isPro(plan) && (
                  <button onClick={()=>setShowUpgradeModal('design')} className="ml-auto text-blue-600 font-medium hover:underline">Upgrade</button>
                )}
              </li>
            </ul>
          </div>


        </div>
      </main>

      {/* Create Team Modal */}
      {showCreateTeamModal && (
        <CreateTeamModal isOpen={showCreateTeamModal} onClose={() => setShowCreateTeamModal(false)} />
      )}

      {/* Upgrade to Pro Modal */}
      {showUpgradeModal && (
        <UpgradeToProModal
          isOpen={true}
          context={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
        />
      )}

      {/* Footer */}
      <AppFooter />
    </div>
  );
};

export default Dashboard; 